import type { DataGrailConsentCategory } from '@/src/lib/datagrail/types';

export type ConsentModeState = 'granted' | 'denied';

export type ConsentModeSignals = {
  ad_storage: ConsentModeState;
  ad_user_data: ConsentModeState;
  ad_personalization: ConsentModeState;
  analytics_storage: ConsentModeState;
};

// How long GTM holds tags waiting for the DataGrail-driven `update` before
// treating the defaults below as final. DataGrail's own readiness poll
// (`waitForConsentApi`) allows 2500ms, but past ~500ms the hold costs more
// measurement than the extra resolved sessions return.
const WAIT_FOR_UPDATE_MS = 500;

// Every signal denies by default and is only released by the DataGrail-driven
// update. Granting analytics up front would let GA4 write `_ga` before a
// `performance` opt-out could be read: `wait_for_update` only holds tags for
// half a second, while DataGrail is allowed 2.5s to answer, so a slow banner
// hands an opted-out session a storage-enabled first pageview. Consent Mode
// keeps sending cookieless pings while denied, so the cost of failing closed
// is modelled sessions rather than lost ones.
const DEFAULT_SIGNALS: ConsentModeSignals = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
};

export function toConsentModeSignals(isGranted: (category: DataGrailConsentCategory) => boolean): ConsentModeSignals {
  const marketing: ConsentModeState = isGranted('marketing') ? 'granted' : 'denied';

  return {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: isGranted('performance') ? 'granted' : 'denied',
  };
}

/**
 * Runs `beforeInteractive`, ahead of gtm.js, so the defaults are on the
 * dataLayer before any GTM tag evaluates consent. Also defines the global
 * `gtag` that `useDataGrailConsentMode` calls for the update — GTM reads the
 * `arguments` object these commands push, which a plain array does not
 * reproduce.
 *
 * `url_passthrough` keeps `gclid`/`wbraid` in URLs when ad consent is denied.
 * HotPads captures no click IDs today, so this is the only thing preserving
 * them across a denied-consent navigation.
 */
export function getConsentModeDefaultScriptContents() {
  const defaults = { ...DEFAULT_SIGNALS, wait_for_update: WAIT_FOR_UPDATE_MS };

  return `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('set', 'url_passthrough', true);
    gtag('consent', 'default', ${JSON.stringify(defaults)});
  `;
}

declare global {
  interface Window {
    dataLayer?: Array<unknown>;
    gtag?: (...args: Array<unknown>) => void;
  }
}
